import './style.scss'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import utility from 'src/utils/utility'
import { Box, Text, tokens } from 'src/componentv2'
import ConfigurationApiContext, { InstitutionDetail } from 'src/provider/API/v2/call-service/configuration'
import ConfigurationHeader, {
    type ConfigurationTabKey, buildConfigTabRoutes,
} from 'src/app/dashboardv2/components/ConfigurationHeader'
import { PAGE_PADDING_X } from 'src/model/v2/interface/constants'

const VALID_TABS: ConfigurationTabKey[] = [
    'profile', 'branding', 'business-logic', 'app-generation', 'feed-notification', 'team-members',
]

const InstitutionConfigurationPage = () => {
    const [searchParams] = useSearchParams()
    const institutionId = searchParams.get('institutionId')
    const tabParam = searchParams.get('tab')
    const navigate = useNavigate()
    const { getInstitutionDetail } = useContext(ConfigurationApiContext)

    const [inst, setInst] = useState<InstitutionDetail | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const activeTab: ConfigurationTabKey = VALID_TABS.includes(tabParam as ConfigurationTabKey)
        ? (tabParam as ConfigurationTabKey)
        : 'profile'

    useEffect(() => {
        if (!institutionId) return
        setIsLoading(true)
        getInstitutionDetail(Number(institutionId))
            .then((data: InstitutionDetail | null) => setInst(data))
            .finally(() => setIsLoading(false))
    }, [institutionId])

    // No tab in the URL (or an invalid one) — settle on ?tab=profile so the
    // header's active state and the URL agree.
    useEffect(() => {
        if (institutionId && tabParam !== activeTab) {
            navigate(buildConfigTabRoutes(institutionId)[activeTab], { replace: true })
        }
    }, [institutionId, tabParam, activeTab])

    const handleTabChange = (nextTab: ConfigurationTabKey) => {
        if (!institutionId) return
        navigate(buildConfigTabRoutes(institutionId)[nextTab])
    }

    const handleBack = () => navigate(`/dashboardv2/institution-details?institutionId=${institutionId}`)

    if (!institutionId) {
        return (
            <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
                <Text fontSize="1.4" color={tokens.textMuted}>No institution selected.</Text>
            </div>
        )
    }

    if (isLoading || !inst) {
        return (
            <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
                <Text fontSize="1.4" color={tokens.textMuted}>Loading configuration...</Text>
            </div>
        )
    }

    return (
        <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <ConfigurationHeader
                institution={{
                    name: inst.name,
                    alias: inst.institutionCode,
                    logoUrl: inst.logo ?? null,
                }}
                activeTab={activeTab}
                onBack={handleBack}
                onTabChange={handleTabChange}
            />

            {/* Tab content — placeholders for now, wired one at a time */}
            <Box topMargin="2.4">
                {activeTab === 'profile' && <Text>Institution Profile content goes here</Text>}
                {activeTab === 'branding' && <Text>Branding content goes here</Text>}
                {activeTab === 'business-logic' && <Text>Business Logic content goes here</Text>}
                {activeTab === 'app-generation' && <Text>App Generation content goes here</Text>}
                {activeTab === 'feed-notification' && <Text>Feed & Notification content goes here</Text>}
                {activeTab === 'team-members' && <Text>Team Members content goes here</Text>}
            </Box>
        </div>
    )
}

export default utility.routeData('institution-configuration', 'Configuration', <InstitutionConfigurationPage />)